<template>
  <default-drawer
    ref="sidebarRef"
    title="Updates"
    v-bind="$attrs"
    @close="closeSidebar"
  >
    <div class="tw-px-6 tw-py-4">
      <c-banner v-if="!isLoading && !features.length"
        >Er zijn (nog) geen updates gevonden.</c-banner
      >

      <loading-icon v-if="isLoading && !features.length" />

      <div v-else class="tw-space-y-8">
        <div
          class="tw-rounded-lg tw-bg-slate-50 tw-px-4 tw-py-2 tw-drop-shadow"
          v-for="{
            id,
            title,
            content,
            is_new,
            published_at,
          } in formattedFeatures"
          :key="id"
        >
          <q-badge v-if="is_new" floating color="red">Nieuw</q-badge>

          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
            <h1 class="h1">{{ title }}</h1>
          </div>

          <c-rich-text :content />

          <div class="tw-text-right tw-text-xs">
            {{ publishDate(published_at) }}
          </div>
        </div>

        <template v-if="error">
          <c-banner type="error">{{ error }}</c-banner>
        </template>

        <div
          class="tw-mt-4 tw-flex tw-justify-center"
          v-if="!isLoading && features.length"
        >
          <lazy-load-trigger
            :load-more="loadNextPage"
            :is-loading="isLoading"
            :has-more="hasMore"
            trigger="auto"
          />
        </div>
      </div>
    </div>
  </default-drawer>
</template>

<script setup>
import DefaultDrawer from "components/wrappers/drawer/DefaultDrawer.vue";
import { computed, onMounted, useTemplateRef } from "vue";
import LoadingIcon from "components/loaders/LoadingIcon.vue";
import { useFeaturesStore } from "store/modules/featuresStore.js";
import { usePageLazyLoad } from "composables/usePageLazyLoad.js";
import LazyLoadTrigger from "components/global/LazyLoadTrigger.vue";
import { toDutchDateString } from "common/dateHelpers.js";

// Refs
const sidebarRef = useTemplateRef("sidebarRef");

// Props
defineProps({
  title: {
    type: String,
    default: null,
  },
});

// Store
const featuresStore = useFeaturesStore();
const { fetchFeatures: useFetchFeatures } = featuresStore;

const decodePreIframe = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  doc.querySelectorAll("pre").forEach((pre) => {
    let raw = pre.innerHTML.trim();

    // Step 1: Remove wrapping quotes if present
    if (
      (raw.startsWith('"') && raw.endsWith('"')) ||
      (raw.startsWith("'") && raw.endsWith("'"))
    ) {
      raw = raw.slice(1, -1);
    }

    // Step 2: Decode HTML entities
    const temp = document.createElement("textarea");
    temp.innerHTML = raw;
    const decodedHtml = temp.value; // Now we have proper <iframe>...</iframe>

    // Step 3: Parse it as real HTML and replace the pre tag
    const fragment = parser.parseFromString(decodedHtml, "text/html");
    const iframe = fragment.querySelector("iframe");

    if (iframe) {
      // Optional: add Tailwind classes to iframe itself
      iframe.classList.add("tw-aspect-video", "tw-w-full");

      // Step 4: Create responsive wrapper div
      const wrapper = document.createElement("div");
      wrapper.appendChild(iframe);

      // Step 5: Replace <pre> with wrapper
      pre.replaceWith(wrapper);
    }
  });

  return doc.body.innerHTML;
};

const formattedFeatures = computed(() => {
  return features.value.map((feature) => {
    feature.content = decodePreIframe(feature.content);

    return feature;
  });
});

// Lazy Load
const {
  data: features,
  loadNextPage,
  isLoading,
  error,
  hasMore,
} = usePageLazyLoad(
  async ({ pagination }) => {
    return await useFetchFeatures({
      pagination,
      forceFetch: true,
    });
  },
  1,
  5,
);

const publishDate = (date = null) => {
  if (!date) {
    return "Niet gepubliceerd";
  }

  if (isNaN(new Date(date))) {
    throw new Error(`Invalid date format for published_at: ${date} `);
  }

  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "full",
    timeZone: "Europe/Amsterdam",
  }).format(new Date(date));
};

onMounted(() => {
  loadNextPage();
});

const closeSidebar = () => {
  sidebarRef.value.close();
};
</script>
